import type { TelemetryOptions } from "@smakecloud/browser-telemetry";

// Browser telemetry (Grafana Faro, @smakecloud/browser-telemetry): the plugin is
// plugins/00.telemetry.client.ts, the beacons go to the same-origin /collect route.

/** Faro app name = browser service.name, never the server's "merchant-administration". */
export const TELEMETRY_APP_NAME = "merchant-administration-browser";

/**
 * Requests that get no span. They are matched against the full URL. Covers
 * pollers, websockets, third-party scripts, the long-running streams (a span
 * would last as long as the stream) and the presigned S3 uploads.
 */
export const TELEMETRY_IGNORE_URLS: readonly RegExp[] = [
    /\/_nuxt\//,
    /\/collect$/,
    /pusher|sockjs/,
    /contentsquare|hotjar/,
    /fontawesome/,
    // SSE: queue task events, AI vectorization and AI SVG edit
    /\/api\/vectorizeTasks\/[^/]+\/events/,
    /\/api\/imageProcessor\/(?:aiVectorizeFull|aiSvgEdit)\b/,
    // presigned PUTs (bucket.s3.<region>.amazonaws.com); anchored to the host,
    // so API calls with an S3 URL in their query string keep their span
    /^https:\/\/[^/?#]+\.amazonaws\.com(?:[/?#]|$)/,
];

export type FaroSessionApi = {
    api: {
        getSession: () => FaroSession | undefined;
        setSession: (session: FaroSession) => void;
    };
};

export type TelemetryConfig = {
    merchantApiBaseUrl: string;
    telemetry: {
        enabled: boolean | string;
        environment: string;
        sampleRate: number | string;
        version: number | string;
    };
};

type FaroSession = {
    id?: string;
    attributes?: Record<string, string>;
};

/**
 * The merchant slug as a session attribute. Ids only: merchant users are
 * customers, so no user id until the API provides a server-side telemetry id.
 * The session id stays the same, so the sampling decision is kept.
 */
export function setSessionSlug(
    faro: FaroSessionApi,
    slug: string | undefined,
): void {
    if (!slug) {
        return;
    }

    const session = faro.api.getSession();

    if (session?.attributes?.slug === slug) {
        return;
    }

    faro.api.setSession({
        ...session,
        attributes: { ...session?.attributes, slug },
    });
}

/**
 * Faro options from the public runtime config, or undefined when telemetry is
 * off. traceparent only goes to the merchant API (smake CORS allows it); the
 * designer's edge-api, S3 and third parties get none.
 */
export function telemetryOptions({
    merchantApiBaseUrl,
    telemetry,
}: TelemetryConfig): TelemetryOptions | undefined {
    if (telemetry.enabled !== true && telemetry.enabled !== "true") {
        return undefined;
    }

    return {
        apiOrigins: [merchantApiBaseUrl],
        appName: TELEMETRY_APP_NAME,
        collectorUrl: "/collect",
        environment: telemetry.environment,
        ignoreUrls: TELEMETRY_IGNORE_URLS,
        sampleRate: telemetry.sampleRate,
        version: String(telemetry.version),
    };
}
